/* =========================================================================
 * THE ONE DATE FILTER — pure model, ZERO IMPORTS. [Jarvis · Claude] 2026-10-26
 *
 * Brian: "should be able to search by a date in all ... available tools like
 * the now, soon, later tab." One model, one component (DateFilter.jsx), one
 * backend parameter (`?from=&to=&undated=`, sr-portal-backend date-range.js).
 *
 * THREE RULES, each from a standing ruling:
 *  1. A record with NO DATE never vanishes (no-invisible-records). It is kept
 *     by default and counted; hiding it is an explicit choice the summary
 *     still states.
 *  2. A PROJECTED date is labelled projected (schedule-is-projected-not-real).
 *     It can match a range — that is what a projection is for — but it is
 *     counted on its own and never reported as an event.
 *  3. Every date shown carries its YEAR (championship-date-includes-the-year).
 *
 * Each surface says WHICH date it filters on (see SURFACE_DATES below); the
 * control prints that name so "Oct 26, 2026" is never ambiguous.
 * ========================================================================= */

const S = (v) => String(v ?? '').trim()
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const MS = 86400000

/** A Date → local `YYYY-MM-DD` (the calendar day the person is looking at). */
export function dayKey(d) {
  if (!(d instanceof Date) || !Number.isFinite(d.getTime())) return null
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** `YYYY-MM-DD` → the same string if it is a real calendar day, else null. */
export function validDay(v) {
  const m = DAY_RE.exec(S(v))
  if (!m) return null
  const d = new Date(+m[1], +m[2] - 1, +m[3])
  return d.getFullYear() === +m[1] && d.getMonth() === +m[2] - 1 && d.getDate() === +m[3] ? S(v) : null
}

/** Anything date-like (Date, ISO, epoch, YYYY-MM-DD) → `YYYY-MM-DD` or null. */
export function toDay(v) {
  if (v == null || v === '') return null
  if (v instanceof Date) return dayKey(v)
  if (typeof v === 'number') return dayKey(new Date(v))
  const s = S(v)
  if (validDay(s.slice(0, 10)) && (s.length === 10 || s[10] === 'T')) {
    // A bare day stays that day; a timestamp is read in local time.
    return s.length === 10 ? s : dayKey(new Date(s))
  }
  const t = Date.parse(s)
  return Number.isFinite(t) ? dayKey(new Date(t)) : null
}

const addDays = (day, n) => {
  const [y, m, d] = day.split('-').map(Number)
  return dayKey(new Date(y, m - 1, d + n))
}

/** Day key → "Oct 26, 2026". ALWAYS with the year. */
export function formatDay(day) {
  const v = validDay(day)
  if (!v) return ''
  const [y, m, d] = v.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/* ---- presets: the Now / Soon / Later language, plus a look back ------- */
export const PRESETS = Object.freeze([
  { key: 'all', label: 'All dates' },
  { key: 'past30', label: 'Last 30 days' },
  { key: 'now', label: 'Now · next 7 days' },
  { key: 'soon', label: 'Soon · 8–30 days' },
  { key: 'later', label: 'Later · 31+ days' },
  { key: 'day', label: 'Pick a date' },
  { key: 'range', label: 'Date range' },
])
export const UNDATED = Object.freeze(['include', 'exclude', 'only'])
export const EMPTY_DATE_FILTER = Object.freeze({ preset: 'all', from: '', to: '', undated: 'include' })

/** Choosing a dated preset leaves the mutually exclusive 'No date' view.
 * All dates resets the whole date lens; other presets retain an explicit hide.
 */
export function selectDatePreset(filter, preset) {
  if (preset === 'all') return { ...EMPTY_DATE_FILTER }
  const current = { ...EMPTY_DATE_FILTER, ...(filter || {}) }
  return { ...current, preset, undated: current.undated === 'only' ? 'include' : current.undated }
}

/** The concrete inclusive range a filter means today. `null` = no range. */
export function resolveRange(filter = EMPTY_DATE_FILTER, now = new Date()) {
  const today = dayKey(now)
  const f = { ...EMPTY_DATE_FILTER, ...(filter || {}) }
  switch (f.preset) {
    case 'past30': return { from: addDays(today, -30), to: addDays(today, -1) }
    case 'now': return { from: today, to: addDays(today, 7) }
    case 'soon': return { from: addDays(today, 8), to: addDays(today, 30) }
    case 'later': return { from: addDays(today, 31), to: null }
    case 'day': return validDay(f.from) ? { from: f.from, to: f.from } : null
    case 'range': {
      let from = validDay(f.from), to = validDay(f.to)
      if (from && to && from > to) [from, to] = [to, from]
      return from || to ? { from, to } : null
    }
    default: return null
  }
}

/** Is the filter doing anything at all? */
export const isActive = (filter, now) => !!resolveRange(filter, now) || (filter?.undated && filter.undated !== 'include')

/** "Oct 26, 2026 – Nov 2, 2026", "from Nov 26, 2026", "Oct 26, 2026". */
export function describeRange(range) {
  if (!range) return 'all dates'
  if (range.from && range.to && range.from === range.to) return formatDay(range.from)
  if (range.from && range.to) return `${formatDay(range.from)} – ${formatDay(range.to)}`
  if (range.from) return `from ${formatDay(range.from)}`
  return `through ${formatDay(range.to)}`
}

/**
 * Apply the filter. `dateOf(row)` → `{ day, projected }` (or a bare date).
 * Returns rows in their ORIGINAL order plus a summary that reconciles:
 * inRange + undatedShown + outOfRange + undatedHidden === total.
 */
export function applyDateFilter(rows = [], filter = EMPTY_DATE_FILTER, dateOf, now = new Date()) {
  const range = resolveRange(filter, now)
  const undated = UNDATED.includes(filter?.undated) ? filter.undated : 'include'
  const total = rows.length
  const sum = { total, range, undated, inRange: 0, projectedInRange: 0, undatedTotal: 0,
    undatedShown: 0, undatedHidden: 0, outOfRange: 0 }
  if (!range && undated === 'include') {
    for (const r of rows) {
      const date = readDate(dateOf, r)
      if (!date.day) sum.undatedTotal++
      else if (date.projected) sum.projectedInRange++
    }
    sum.inRange = total - sum.undatedTotal
    sum.undatedShown = sum.undatedTotal
    return { rows, summary: sum }
  }
  const kept = []
  for (const r of rows) {
    const { day, projected } = readDate(dateOf, r)
    if (!day) {
      sum.undatedTotal++
      if (undated === 'exclude') { sum.undatedHidden++; continue }
      sum.undatedShown++; kept.push(r); continue
    }
    if (undated === 'only') { sum.outOfRange++; continue }
    if (range && ((range.from && day < range.from) || (range.to && day > range.to))) { sum.outOfRange++; continue }
    sum.inRange++; if (projected) sum.projectedInRange++
    kept.push(r)
  }
  return { rows: kept, summary: sum }
}

function readDate(dateOf, r) {
  const got = dateOf ? dateOf(r) : null
  if (got && typeof got === 'object' && !(got instanceof Date)) return { day: toDay(got.day), projected: !!got.projected }
  return { day: toDay(got), projected: false }
}

/** The same filter as the backend's query parameters. */
export function rangeToQuery(filterOrRange, now = new Date()) {
  if (!filterOrRange) return {}
  const range = 'preset' in filterOrRange ? resolveRange(filterOrRange, now) : filterOrRange
  const undated = filterOrRange.undated && filterOrRange.undated !== 'include' ? filterOrRange.undated : undefined
  return { from: range?.from || undefined, to: range?.to || undefined, undated }
}

/* ---- URL state: `dpre`, `dfrom`, `dto`, `dund` ------------------------
 * Distinct names on purpose: the Command Center already owns `day` (the
 * calendar's selected day) and a shared name would make one control drive
 * the other. */
export function readDateFilter(params) {
  const get = (k) => (params && typeof params.get === 'function' ? params.get(k) : params?.[k]) || ''
  const preset = PRESETS.some((p) => p.key === get('dpre')) ? get('dpre') : 'all'
  const undated = UNDATED.includes(get('dund')) ? get('dund') : 'include'
  return { preset, from: validDay(get('dfrom')) || '', to: validDay(get('dto')) || '', undated }
}

export function writeDateFilter(previous, filter) {
  const next = new URLSearchParams(previous)
  const f = { ...EMPTY_DATE_FILTER, ...(filter || {}) }
  for (const k of ['dpre', 'dfrom', 'dto', 'dund']) next.delete(k)
  if (f.preset && f.preset !== 'all') next.set('dpre', f.preset)
  if ((f.preset === 'day' || f.preset === 'range') && validDay(f.from)) next.set('dfrom', f.from)
  if (f.preset === 'range' && validDay(f.to)) next.set('dto', f.to)
  if (f.undated && f.undated !== 'include') next.set('dund', f.undated)
  next.delete('page')
  return next
}

/* ---- WHICH DATE EACH SURFACE FILTERS ON --------------------------------
 * One line per surface. `projected` is how that surface knows a date is a
 * guess; a surface whose dates are all real event timestamps says so.
 * Read by the component (to print the name) and by the inventory doc. */
const prop = (o, k) => o?.[k]
const championshipEvent = (c) => ({
  day: toDay(c?.effDate),
  projected: !!c?.effDate && c?.dateConfidence !== 'confirmed',
})
export const SURFACE_DATES = Object.freeze({
  championships: { label: 'Event date', dateOf: championshipEvent,
    note: 'The event date the card shows (confirmed → event → projected, rolled to the next occurrence). Anything not confirmed is counted as projected.' },
  crowns: { label: 'Crown date', dateOf: (c) => ({ day: toDay(prop(c, 'crownedAt') || prop(c, 'championDetectedAt')), projected: false }),
    note: 'When the champion was captured on the record (sr_winner_captured_date; the hunt’s detection time when that is blank).' },
  hunted: { label: 'Hunt date', dateOf: (c) => ({ day: toDay(prop(c, 'championSearchedAt')), projected: false }),
    note: 'When the champion hunt last searched the record.' },
  rfp: { label: 'Close date', dateOf: (c) => ({ day: toDay(c?.effDate || c?.closeDate), projected: false }),
    note: 'The RFP close date.' },
  pursuitOpened: { label: 'Pursuit date', dateOf: (c) => ({ day: toDay(c?.pursuitDate || c?.openDate), projected: !!c?.openDateIsProxy }),
    note: 'When the pursuit opened. The RFP engine’s scan date is a proxy and is counted as projected.' },
  cheer: { label: 'Last title date', dateOf: (c) => ({ day: toDay(c?.lastTitleAt), projected: false }),
    note: 'The most recent championship a gym’s teams won.' },
  worklist: { label: 'Due / event date', dateOf: championshipEvent,
    note: 'The record’s event date; projected dates are counted as projected.' },
  orders: { label: 'Due date', dateOf: (o) => ({ day: toDay(o?.dueDate), projected: false }),
    note: 'The ClickUp order due date.' },
  reviews: { label: 'Delivered date', dateOf: (r) => ({ day: toDay(r?.deliveredAt || r?.receivedAt), projected: false }),
    note: 'When the order was delivered (or the review received).' },
  trades: { label: 'Championship date', dateOf: (t) => {
    // The desk serves `window.daysToEvent` (relative to its read), not a day,
    // so the day is today + that count. `governedByUnsourced` = projected.
    const explicit = toDay(t?.eventDate || t?.window?.eventDate)
    const dte = t?.window?.daysToEvent
    const day = explicit || (Number.isFinite(dte) ? dayKey(new Date(Date.now() + dte * MS)) : null)
    return { day, projected: !!(t?.window?.governedByUnsourced || t?.window?.projected) }
  }, note: 'The earliest championship the offer covers (the trade window closes on it). A projected date is counted as projected.' },
  bowls: { label: 'Bowl-eligible date', dateOf: (t) => ({ day: toDay(t?.becameAt), projected: false }),
    note: 'When the team became bowl-eligible.' },
  jobs: { label: 'Next action due', dateOf: (j) => ({ day: toDay(j?.nextAction?.dueAt || j?.dueAt), projected: false }),
    note: 'When the artwork job’s next action is due.' },
  moves: { label: 'Moved at', dateOf: (m) => ({ day: toDay(m?.at), projected: false }),
    note: 'When the card was moved on the Pursuit board.' },
})

/** Which date a Command Center view filters on. */
export function ccSurfaceFor(view, category, dated = true) {
  if (view === 'justwon') return 'crowns'
  if (view === 'hunted') return 'hunted'
  if (category?.key === 'rfp') return 'rfp'
  if (category?.key === 'cheer') return 'cheer'
  // Corporate / police / fire / government / military / letterwinner / Greek
  // carry no event date; the date they DO carry is when the pursuit opened.
  if (!dated) return 'pursuitOpened'
  return 'championships'
}
